import { useState, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { SUPPORTED_CURRENCIES, DEFAULT_CURRENCY_CODE } from '../components/SeedCalculator/types';

const LS_CURRENCY_KEY = 'spi-selected-currency';
const LS_RATES_KEY = 'spi-exchange-rates';

interface ExchangeRatesResponse {
  result: string;
  rates: Record<string, number>;
}

function getStoredCurrency(): string {
  try {
    return localStorage.getItem(LS_CURRENCY_KEY) || DEFAULT_CURRENCY_CODE;
  } catch {
    return DEFAULT_CURRENCY_CODE;
  }
}

function getStoredRates(): Record<string, number> | null {
  try {
    const raw = localStorage.getItem(LS_RATES_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function getFallbackRate(code: string): number {
  const currency = SUPPORTED_CURRENCIES.find((c) => c.code === code);
  return currency?.fallbackRate ?? 1;
}

async function fetchExchangeRates(): Promise<Record<string, number>> {
  const response = await fetch('https://open.er-api.com/v6/latest/USD');
  if (!response.ok) throw new Error('Failed to fetch exchange rates');
  const data: ExchangeRatesResponse = await response.json();
  if (data.result !== 'success') throw new Error('API returned error');
  return data.rates;
}

export function useCurrency(initialCode?: string) {
  const [currencyCode, setCurrencyCodeState] = useState<string>(
    initialCode || getStoredCurrency()
  );

  const setCurrencyCode = useCallback((code: string) => {
    setCurrencyCodeState(code);
    try {
      localStorage.setItem(LS_CURRENCY_KEY, code);
    } catch {
      // localStorage unavailable
    }
  }, []);

  const { data: liveRates, isSuccess: isLive, isLoading } = useQuery({
    queryKey: ['exchange-rates'],
    queryFn: fetchExchangeRates,
    staleTime: 1000 * 60 * 60, // 1 hour
    gcTime: 1000 * 60 * 60 * 24, // 24 hours
    retry: 2,
    refetchOnWindowFocus: false,
  });

  // Cache live rates to localStorage when available
  if (liveRates) {
    try {
      localStorage.setItem(LS_RATES_KEY, JSON.stringify(liveRates));
    } catch {
      // localStorage unavailable
    }
  }

  // Fallback cascade: live → cached → hardcoded
  let exchangeRate: number;
  if (liveRates && liveRates[currencyCode]) {
    exchangeRate = liveRates[currencyCode];
  } else {
    const cached = getStoredRates();
    if (cached && cached[currencyCode]) {
      exchangeRate = cached[currencyCode];
    } else {
      exchangeRate = getFallbackRate(currencyCode);
    }
  }

  return {
    currencyCode,
    setCurrencyCode,
    exchangeRate,
    isLive,
    isLoading,
  };
}
